/**
 * Server-Sent Events (SSE) 路由
 * 提供实时事件推送
 */

import { Router } from 'express';
import { EventEmitter } from 'events';
import { SSEEventType, SSEEvent } from '../types.js';
import { apiConfig } from '../config.js';
import { logger } from '../../utils/logger.js';
import { getRealtimeSyncManager } from '../../core/realtime-sync.js';

const router = Router();

// 全局事件发射器
export const sseEventBus = new EventEmitter();
sseEventBus.setMaxListeners(apiConfig.sse.maxConnections);

// 客户端连接管理
interface SSEClient {
  id: string;
  res: any;
  userId?: string;
  filters?: string[];
  lastEventId?: string;
}

const clients = new Map<string, SSEClient>();

// SSE 连接端点
router.get('/connect', handleSSEConnection);
router.get('/subscribe', handleSSEConnection); // 别名，兼容前端

function handleSSEConnection(req: any, res: any) {
  // 设置 SSE 响应头
  res.writeHead(200, {
    'Content-Type': 'text/event-stream',
    'Cache-Control': 'no-cache',
    'Connection': 'keep-alive',
    'X-Accel-Buffering': 'no', // 禁用 Nginx 缓冲
  });

  // 创建客户端 ID
  const clientId = `sse-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const userId = (req.query.userId as string) || undefined;
  const filters = req.query.filters 
    ? (Array.isArray(req.query.filters) ? req.query.filters : [req.query.filters]) 
    : [];

  // 保存客户端连接
  const client: SSEClient = {
    id: clientId,
    res,
    userId,
    filters: filters as string[],
    lastEventId: req.headers['last-event-id'] as string,
  };
  clients.set(clientId, client);

  // 注册到 RealtimeSyncManager
  const realtimeSync = getRealtimeSyncManager();
  realtimeSync.addSSEClient(clientId, res, userId);

  logger.info(`SSE client connected: ${clientId}`, { userId, filters });

  // 发送初始连接成功消息
  sendEvent(res, {
    type: SSEEventType.SYSTEM_NOTIFICATION,
    data: {
      message: 'Connected to Workflow SSE',
      clientId,
      timestamp: new Date().toISOString(),
    },
    timestamp: new Date().toISOString(),
  });

  // 设置心跳
  const heartbeatInterval = setInterval(() => {
    sendEvent(res, {
      type: SSEEventType.HEARTBEAT,
      data: { timestamp: new Date().toISOString() },
      timestamp: new Date().toISOString(),
    });
  }, apiConfig.sse.heartbeatInterval);

  // 处理客户端断开
  req.on('close', () => {
    clearInterval(heartbeatInterval);
    clients.delete(clientId);
    // 从 RealtimeSyncManager 移除
    realtimeSync.removeSSEClient(clientId);
    logger.info(`SSE client disconnected: ${clientId}`);
  });

  // 处理错误
  req.on('error', (error) => {
    logger.error(`SSE client error: ${clientId}`, error);
    clearInterval(heartbeatInterval);
    clients.delete(clientId);
    // 从 RealtimeSyncManager 移除
    realtimeSync.removeSSEClient(clientId);
  });
}

// 发送事件到指定客户端
function sendEvent(res: any, event: SSEEvent): void {
  try {
    const eventString = formatSSEEvent(event);
    res.write(eventString);
  } catch (error) {
    logger.error('Error sending SSE event:', error);
  }
}

// 格式化 SSE 事件
function formatSSEEvent(event: SSEEvent): string {
  let eventString = '';
  
  if (event.id) {
    eventString += `id: ${event.id}\n`;
  }
  
  eventString += `event: ${event.type}\n`;
  eventString += `data: ${JSON.stringify(event.data)}\n\n`;
  
  return eventString;
}

// 广播事件到所有客户端
export function broadcastEvent(event: SSEEvent): void {
  clients.forEach((client) => {
    // 检查过滤器
    if (client.filters && client.filters.length > 0) {
      const eventTypePrefix = event.type.split('.')[0];
      if (!client.filters.includes(eventTypePrefix) && !client.filters.includes('*')) {
        return;
      }
    }
    
    sendEvent(client.res, event);
  });
}

// 发送事件到特定用户
export function sendEventToUser(userId: string, event: SSEEvent): void {
  clients.forEach((client) => {
    if (client.userId === userId) {
      sendEvent(client.res, event);
    }
  });
}

// 监听事件总线
sseEventBus.on('task.created', (data) => {
  broadcastEvent({
    type: SSEEventType.TASK_CREATED,
    data,
    timestamp: new Date().toISOString(),
  });
});

sseEventBus.on('task.updated', (data) => {
  broadcastEvent({
    type: SSEEventType.TASK_UPDATED,
    data,
    timestamp: new Date().toISOString(),
  });
});

sseEventBus.on('task.deleted', (data) => {
  broadcastEvent({
    type: SSEEventType.TASK_DELETED,
    data,
    timestamp: new Date().toISOString(),
  });
});

sseEventBus.on('workflow.started', (data) => {
  broadcastEvent({
    type: SSEEventType.WORKFLOW_STARTED,
    data,
    timestamp: new Date().toISOString(),
  });
});

sseEventBus.on('workflow.completed', (data) => {
  broadcastEvent({
    type: SSEEventType.WORKFLOW_COMPLETED,
    data,
    timestamp: new Date().toISOString(),
  });
});

sseEventBus.on('workflow.failed', (data) => {
  broadcastEvent({
    type: SSEEventType.WORKFLOW_FAILED,
    data,
    timestamp: new Date().toISOString(),
  });
});

// 管理端点
router.get('/clients', (req, res) => {
  const clientList = Array.from(clients.values()).map(client => ({
    id: client.id,
    userId: client.userId,
    filters: client.filters,
    connected: true,
  }));
  
  res.success({
    total: clientList.length,
    clients: clientList,
  });
});

// 发送自定义事件
router.post('/broadcast', (req, res) => {
  const { type, data } = req.body;
  
  if (!type || !data) {
    res.error('VALIDATION_ERROR', 'Type and data are required', 400);
    return;
  }
  
  const event: SSEEvent = {
    type: type as SSEEventType,
    data,
    timestamp: new Date().toISOString(),
  };
  
  broadcastEvent(event);
  
  res.success({
    broadcasted: true,
    clientCount: clients.size,
  });
});

export { router as sseRouter };